import { SEO } from "../components/SEO";
import { SITE } from "../config/site";

function LegalShell({ title, path, description, updated, children, testid }) {
  return (
    <div data-testid={testid}>
      <SEO title={title} path={path} description={description} />
      <div className="bg-brand-forest py-14 text-brand-cream">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h1 className="text-4xl font-black sm:text-5xl">{title}</h1>
          <p className="mt-3 text-xs uppercase tracking-[0.25em] text-brand-cream/50">Last updated · {updated}</p>
        </div>
      </div>
      <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
        <div className="space-y-8 text-sm leading-relaxed text-brand-ink/70">{children}</div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="mb-3 font-display text-xl font-bold text-brand-forest">{title}</h2>
      {children}
    </section>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy" path="/privacy-policy" testid="privacy-page"
      description="How CannaCheap DC collects, uses and protects your information." updated="July 2026">
      <Section title="What we collect">
        <p>When you place an order or contact us, we collect the information needed to fulfill your order: your name, contact details, delivery address and order contents. Age-verification information (such as a photo of your government ID, where required) is used solely to confirm you are 21 or older and is never used for marketing.</p>
      </Section>
      <Section title="How we use it">
        <p>We use your information to process and deliver orders, confirm your eligibility, respond to questions, and — only if you opt in — send promotions and updates. We do not sell your personal information to anyone, ever.</p>
      </Section>
      <Section title="Checkout & payment">
        <p>Checkout and payment processing are handled by our secure store platform, which maintains its own privacy and security practices for payment and order data. We never see or store full payment credentials.</p>
      </Section>
      <Section title="Your choices">
        <p>You can unsubscribe from promotional emails at any time, and you can ask us to review or delete your information by emailing {SITE.email}.</p>
      </Section>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms & Conditions" path="/terms" testid="terms-page"
      description="Terms of service for ordering from CannaCheap DC." updated="July 2026">
      <Section title="Eligibility">
        <p>{SITE.name} serves adults 21 years of age and older only. By using this website and placing an order, you confirm that you are at least 21 and that the information you provide is accurate. A valid government-issued photo ID is required at the time of delivery or pickup — orders that cannot be verified will be cancelled.</p>
      </Section>
      <Section title="Orders & availability">
        <p>All products are subject to availability. Prices, deals and menu items may change without notice. Order confirmation is provided by our team after checkout; an order is not final until confirmed.</p>
      </Section>
      <Section title="Delivery & pickup">
        <p>Delivery windows are estimates, typically 30–90 minutes depending on location and volume. Delivery is free on orders of ${SITE.freeDeliveryMinimum} or more. You must be present with valid ID to receive a delivery.</p>
      </Section>
      <Section title="Payments">
        <p>We accept {SITE.payments.join(", ")}. Payment details are confirmed during the secure checkout process.</p>
      </Section>
      <Section title="Responsible use">
        <p>Products are intended for responsible adult use in accordance with applicable local laws. Do not drive or operate machinery after use. Keep all products away from children and pets.</p>
      </Section>
    </LegalShell>
  );
}

export function AgeEligibilityPage() {
  return (
    <LegalShell title="Age & Eligibility" path="/age-eligibility" testid="age-eligibility-page"
      description="CannaCheap DC serves adults 21+ only. Learn how age verification works for delivery and pickup orders." updated="July 2026">
      <Section title="21+ only">
        <p>{SITE.name} is strictly for adults 21 years of age and older. This applies to every product on the menu and every order, without exception.</p>
      </Section>
      <Section title="How verification works">
        <p>Depending on the product, you may be asked to confirm your age during secure checkout — for example by uploading a photo of yourself holding your government-issued ID. In all cases, a valid government-issued photo ID is verified again at the moment of delivery or pickup. If verification cannot be completed, the order is cancelled and the products are returned.</p>
      </Section>
      <Section title="Your privacy">
        <p>Age-verification information is used only to confirm eligibility and is handled securely. We collect the minimum information necessary and never use ID documents for any other purpose.</p>
      </Section>
      <Section title="Questions">
        <p>
          Questions about eligibility? Call us at <a href={SITE.phoneHref} className="font-bold text-brand-pine underline underline-offset-2">{SITE.phone}</a> or
          email <a href={`mailto:${SITE.email}`} className="font-bold text-brand-pine underline underline-offset-2">{SITE.email}</a>.
        </p>
      </Section>
    </LegalShell>
  );
}
